const ITEM_ID_PATTERN = /^[a-f0-9]{64}$/;
const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const PLACEMENT_NAMESPACE = "opaque-id:item-placement:v1";

export class ItemPlacementReferenceError extends Error {
  constructor(code = "failed-precondition") {
    super("This listing cannot be opened securely.");
    this.name = "ItemPlacementReferenceError";
    this.code = code;
  }
}

function fail(code) {
  throw new ItemPlacementReferenceError(code);
}

function normalizeInput(value) {
  const prototype =
    value && typeof value === "object" ? Object.getPrototypeOf(value) : null;
  const ownKeys =
    value && typeof value === "object" ? Reflect.ownKeys(value) : [];
  const descriptors =
    value && typeof value === "object"
      ? Object.getOwnPropertyDescriptors(value)
      : {};
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    (prototype !== Object.prototype && prototype !== null) ||
    ownKeys.length !== 2 ||
    ownKeys.some(
      (key) =>
        typeof key !== "string" ||
        !descriptors[key]?.enumerable ||
        !Object.prototype.hasOwnProperty.call(descriptors[key], "value")
    ) ||
    !Object.prototype.hasOwnProperty.call(value, "itemId") ||
    !Object.prototype.hasOwnProperty.call(value, "orgId") ||
    typeof value.itemId !== "string" ||
    !ITEM_ID_PATTERN.test(value.itemId) ||
    typeof value.orgId !== "string" ||
    !ORG_ID_PATTERN.test(value.orgId)
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({ itemId: value.itemId, orgId: value.orgId });
}

/**
 * Derives the public item-placement reference used as the listing identifier.
 * This is an identifier only: settlement authority still comes exclusively
 * from the private server graph loaded by quoteListingPurchase.
 */
export async function deriveItemPlacementReference(
  inputValue,
  cryptoProvider = globalThis.crypto
) {
  const input = normalizeInput(inputValue);
  if (
    typeof TextEncoder !== "function" ||
    typeof cryptoProvider?.subtle?.digest !== "function"
  ) {
    fail("failed-precondition");
  }

  const encoder = new TextEncoder();
  const namespaceLength = encoder.encode(PLACEMENT_NAMESPACE).byteLength;
  const canonicalComponents = JSON.stringify([input.itemId, input.orgId]);
  const payload = encoder.encode(
    `unit-authority\0${namespaceLength}:${PLACEMENT_NAMESPACE}:${canonicalComponents}`
  );
  let digest;
  try {
    digest = await cryptoProvider.subtle.digest("SHA-256", payload);
  } catch (_error) {
    fail("failed-precondition");
  }
  const bytes = new Uint8Array(digest);
  if (bytes.length !== 32) fail("failed-precondition");
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
}
